import { FC, useCallback, useEffect, useRef, useState } from "react";

import IDataPreventivoProps from "pages/Prenotazione/Form/DataPreventivo/IDataPreventivoProps";

import { OrariRepository } from "core/data/index";

import { IOrario } from "core/interfaces/data/IOrariRequest";

import { CalendarStrip, Reservation, CustomButton, CustomErrorMessage } from "core/interfaces/components/index";
import { Alert, Skeleton, Spin } from "antd";

import moment, { Moment } from "moment";

import "moment/locale/it";
import "./DataPreventivo.css";
import { IOperatorAvailabilitySetAllocatedRequest, ITariffa } from "core/interfaces/data/IDataPreventivoRequest";
import DataPreventivoRepository from "core/data/DataPreventivoRepository";
import { CustomButtonType } from "components/CustomButton/CustomButtonType";

import { setOrario, setProfessionista, setTariffa } from "core/redux/features/dataForm/dataFormSlice";
import { getReduxOrario } from "utils/ReduxUtils";

import { performAsyncFunction } from "utils/AsyncFunctionUtility";
import NavigationManager from "utils/NavigationManager";
import { Enhanced } from "utils/ReactUtils";
import CustomTranslation from "components/CustomTranslation/CustomTranslation";
import useSession from "utils/Session";
import modal from "antd/lib/modal";
import { IProfessionista } from "core/interfaces/data/IProfessionistiRequest";

const DataPreventivo: FC<IDataPreventivoProps> = ({ dispatch, sessionInfo, t, i18n, dataForm, handleError }) => {
	const { MoveStep } = NavigationManager(sessionInfo);
	const { startSession } = useSession(sessionInfo);

	const [orari, setOrari] = useState<IOrario[] | undefined>(undefined);

	const [selectedDate, setSelectedDate] = useState<Date>();
	const [availableDates, setAvailableDates] = useState<Moment[]>();
	const [loadingDisponibilita, setLoadingDisponibilita] = useState<boolean>(true);

	const [missingReservation, setMissingReservation] = useState<boolean>(false);

	const [listaOrariGiornata, setListaOrariGiornata] = useState<JSX.Element[]>([]);
	const [isOrarioSelected, setIsOrarioSelected] = useState<boolean>(dataForm.orario === undefined);
	const [professionistaOriginale] = useState<IProfessionista>(dataForm.professionista!);

	const startRef: any = useRef(); // aggiunta $ALC

	const SetTariffa = useCallback(
		(tariffa: ITariffa | undefined) => {
			dispatch(setTariffa(tariffa));
		},
		[dispatch]
	);

	const SetOrario = useCallback(
		(orario: IOrario | undefined, scrollToConfirm: boolean) => {
			var orarioRedux = getReduxOrario(orario);//formatta l'orario per la struttura scelta per redux
			dispatch(setOrario(orarioRedux));
			if (scrollToConfirm) {
				var btnConfirm = document.getElementById("btnConfirm");
				var btnPosition = btnConfirm?.getBoundingClientRect();
				var newPosition = (btnPosition?.top ?? 0) - window.innerHeight + 50;
				window.scrollTo({ top: newPosition, behavior: "smooth" });
			}
			setIsOrarioSelected(false);
		},
		[dispatch]
	);

	const GetTariffa = useCallback(async () => {
		const tariffa = await DataPreventivoRepository.calcolaTariffa({
			IdOperatore: dataForm.professionista?.IdOperatore!,
			IguAttivita: dataForm.prestazione?.IguAttivita!,
			culture: i18n.language,
			IdSessione: sessionInfo.sessione?.idSessione!,
			step: sessionInfo.step as number,
		});

		return tariffa;
	}, [dataForm.prestazione?.IguAttivita, dataForm.professionista?.IdOperatore, i18n.language, sessionInfo.sessione?.idSessione, sessionInfo.step]);

	const GetOrari = useCallback(
		async (selectedDate: Date) => {
			const start = moment(selectedDate).startOf("week").isSame(moment().startOf("week"), "day");

			const orari = await OrariRepository.getOrari({
				IdEquipe: dataForm.area?.IdEquipe!,
				IdOperatore: dataForm.professionista?.IdOperatore!,
				IguAttivita: dataForm.prestazione?.IguAttivita!,
				Da: moment(selectedDate).startOf("week").startOf("day").toDate().toWSDateFormat(),
				A: moment(selectedDate).endOf("week").endOf("day").toDate().toWSDateFormat(),
				CodGruppo: dataForm.area?.CodGruppo!,
				culture: i18n.language,
				IdSessione: sessionInfo.sessione?.idSessione!,
				step: sessionInfo.step as number,
			});

			if (orari && start) {
				const orariValidi = orari.filter((orario) => {
					return moment() < moment(orario.Da);
				});

				return orariValidi;
			}

			return orari;
		},
		[sessionInfo.step, dataForm.area, dataForm.prestazione, dataForm.professionista, i18n.language, sessionInfo.sessione]
	);

	const GetDisponibilitaResidue = useCallback(
		async (da: Moment, a: Moment) => {
			const orari = await OrariRepository.getDisponibilitaResidua({
				IdEquipe: dataForm.area?.IdEquipe!,
				IdOperatore: dataForm.professionista?.IdOperatore!,
				IguAttivita: dataForm.prestazione?.IguAttivita!,
				Da: moment(da).startOf("day").toDate().toWSDateFormat(),
				A: moment(a).endOf("day").toDate().toWSDateFormat(),
				CodGruppo: dataForm.area?.CodGruppo!,
				culture: i18n.language,
				IdSessione: sessionInfo.sessione?.idSessione!,
				step: sessionInfo.step as number,
			});

			return orari;
		},
		[sessionInfo.step, dataForm.area, dataForm.prestazione, dataForm.professionista, i18n.language, sessionInfo.sessione]
	);

	const PopolaDisponibilitaSettimanale = useCallback(
		async (dateRif?: Moment) => {
			let newDate = dateRif ? moment(dateRif) : moment(selectedDate);
			var firstDate = newDate;

			if (!availableDates || dateRif) {
				var res: Moment[] = availableDates ?? [];

				var newDateStart = moment(newDate).startOf("week");
				var newDateEnd = moment(newDate).endOf("day").add(1, "months");

				var dateDisponibili = await GetDisponibilitaResidue(newDateStart, newDateEnd);
				if (!dateDisponibili || dateDisponibili!.length === 0) {
					var dataFurtura = moment(new Date("2100-12-31T23:59:59"));
					dateDisponibili = await GetDisponibilitaResidue(newDateStart, dataFurtura);
				}

				res = dateDisponibili!.map((data) => {
					return moment(data);
				});
				setAvailableDates(res);

				if (
					(res?.length ?? 0) > 0 &&
					!res?.find((dt) => {
						return dt.isSame(newDate, "day");
					})
				) {
					firstDate = res![0];
				}

				if (!firstDate.isSame(newDate, "day")) {
					setSelectedDate(firstDate.toDate());
				} else if (!moment(selectedDate).isSame(firstDate, "day") || !selectedDate) {
					setSelectedDate(firstDate.toDate());
				}

				// recupero gli orari

				const orari = await GetOrari(firstDate.toDate());

				setOrari(orari);
				if (!orari) setListaOrariGiornata([]);
			}
		},
		[GetDisponibilitaResidue, GetOrari, availableDates, selectedDate]
	);

	const OnClickDate = async (date: Moment) => {
		if (!date.isSame(moment(selectedDate), "day")) {
			var dt = moment(date).isSameOrBefore(moment(), "day") ? moment().startOf("day") : moment(date).startOf("day");

			setLoadingDisponibilita(true);

			setSelectedDate(moment(dt).toDate());

			setMissingReservation(false);
		}
	};

	const IsSelected = useCallback(
		(reservation: IOrario): boolean => {
			if (!dataForm.orario) return false;

			if (dataForm.professionista && dataForm.professionista.IdOperatore !== reservation.Professionista.IdOperatore) return false;

			if (!moment(dataForm.orario.Da).isSame(reservation.Da, "minute")) return false;

			if (!moment(dataForm.orario.A).isSame(reservation.A, "minute")) return false;

			return true;
		},
		[dataForm.orario, dataForm.professionista]
	);

	const OnSubmit = async () => {
		if (!dataForm.orario) {
			setMissingReservation(true);
			return;
		} else {
			const props: IOperatorAvailabilitySetAllocatedRequest = {
				culture: i18n.language,
				IdSessione: sessionInfo.sessione!.idSessione!,
				step: sessionInfo.step as number,
				IguAttivita: dataForm.prestazione?.IguAttivita!,
				IdOperatore: dataForm.professionista?.IdOperatore!,
				Da: dataForm.orario?.Da!,
				A: dataForm.orario?.A!,
			};

			let res = await DataPreventivoRepository.bloccaDisponibilita(props);

			if (res) {
				await startSession(sessionInfo.step as number);
				MoveStep("next");
			} else {
				modal.warning({
					title: t("riepilogo.popupFail.title"),
					content: t("riepilogo.popupFail.text"),
					onOk() {
						/// aggiungere il ricalcol orari recuperando il professionista orriginale
						dispatch(setProfessionista(professionistaOriginale));
						SetOrario(undefined, false);
						setAvailableDates(undefined);
					},
					okText: t("riepilogo.popupFail.confirm"),
				});
			}
		}
	};

	const getOrariGiornata = useCallback(() => {
		if (orari)
			return orari.filter((orario) => {
				return selectedDate && moment(orario.Da).isSame(moment(selectedDate), "day");
			});
		else return [];
	}, [orari, selectedDate]);

	const clickOrario = useCallback(
		(orarioSelected: IOrario) => {
			SetOrario(orarioSelected, isOrarioSelected);
		},
		[SetOrario, isOrarioSelected]
	);

	const buildListaOrariGiornata = useCallback(() => {
		const list: JSX.Element[] = [];
		const listaOrari = getOrariGiornata();
		for (let [index, el] of listaOrari.entries()) {
			list.push(<Reservation key={index} selected={IsSelected(el)} reservation={el} OnClick={clickOrario} />);
		}
		setListaOrariGiornata(list);
	}, [IsSelected, clickOrario, getOrariGiornata]);

	//#region useEffect
	useEffect(() => {
		setLoadingDisponibilita(true);
		performAsyncFunction(async () => {
			setLoadingDisponibilita(true);

			await PopolaDisponibilitaSettimanale();

			setLoadingDisponibilita(false);
		}, handleError);
	}, [PopolaDisponibilitaSettimanale, handleError]);

	useEffect(() => {
		performAsyncFunction(async () => {
			if (!dataForm.tariffa) {
				// recupero la tariffa
				const tariffa = await GetTariffa();

				SetTariffa(tariffa);
			}
		}, handleError);
	}, [GetTariffa, SetTariffa, dataForm.tariffa, handleError]);

	useEffect(() => {
		if (!loadingDisponibilita) {
			buildListaOrariGiornata();
		}
	}, [buildListaOrariGiornata, loadingDisponibilita]);

	//#endregion useEffect

	useEffect(() => {
		// aggiunta $ALC
		if (startRef.current) startRef.current.focus();
	}, []);

	return (
		<div id="data-preventivo" role="main" aria-label="Step 5">
			<div className="header">
				{/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
				<div className="title" tabIndex={0} ref={startRef}>
					<span>{t("datapreventivo.title")}</span>
				</div>

				<div className="text">
					<span>{t("datapreventivo.text.sceltaOrario")}</span>
				</div>
			</div>
			<div className="date-possibili">
				<>
					{orari && dataForm.tariffa ? (
						<>
							<CalendarStrip
								key={"CalendarStrip_" + i18n.language}
								selectedDate={selectedDate ? moment(selectedDate) : undefined}
								OnClick={OnClickDate}
								AvailableDates={availableDates}
								PopolaDisponibilitaSettimanale={PopolaDisponibilitaSettimanale}
							/>

							<Spin spinning={loadingDisponibilita} size="large">
								{listaOrariGiornata.length !== 0 ? (
									listaOrariGiornata
								) : (
									<div id="no-reservations">
										<CustomTranslation t={t} i18Key={"datapreventivo.text.nessunaDisponibilita"} type="phone"></CustomTranslation>
									</div>
								)}
							</Spin>
							<div className="validator" style={{ display: missingReservation && dataForm.orario === undefined ? "block" : "none" }}>
								<CustomErrorMessage
									text={t("datapreventivo.validationMessages.missingValue")}
									visible={missingReservation && dataForm.orario === undefined ? true : false}
								/>
							</div>
							<br />
							<div className="footer">
								<div className="nome-prestazione text">
									<span>
										{t("datapreventivo.text.prestazione")}: {dataForm.prestazione!.Attivita}
									</span>
								</div>
								<div className="total-box">
									<span>{t("datapreventivo.text.preventivo")}</span>
									<span>
										{!dataForm.tariffa
											? t("tariffa.notSet")
											: dataForm.tariffa?.ImportoTariffaConIva?.toLocaleString(
													undefined, // leave undefined to use the visitor's browser
													// locale or a string like 'en-US' to override it.
													{ style: "currency", currency: "EUR" }
											  )}
									</span>
								</div>
							</div>
							<div className="buttons">
								<CustomButton text={t("backButton")} OnClick={() => MoveStep("prev")} buttonType={CustomButtonType.negative} />

								<CustomButton
									id="btnConfirm"
									text={t("datapreventivo.text.conferma")}
									OnClickAsync={OnSubmit}
									buttonType={CustomButtonType.positive}
									enabled={dataForm.orario !== undefined}
								/>
							</div>
						</>
					) : loadingDisponibilita || !orari || !dataForm.tariffa ? (
						<Skeleton active />
					) : (
						<div className="error">
							<Alert style={{ marginBottom: "3%" }} message={t("errorMessages.base")} type="error" showIcon banner />
							<CustomButton text={t("backButton")} OnClick={() => MoveStep("prev")} buttonType={CustomButtonType.negative} />
						</div>
					)}
				</>
			</div>
		</div>
	);
};

export default Enhanced(DataPreventivo, { componentNamespace: "DataPreventivo" });
